import React, { Fragment, useMemo } from 'react';
import { CategoryTypeString } from '@diritto/common/content/constants';
import { ContentPrivateRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { ProfileName } from 'app-components/contents/ProfileName';
import { AdultRoundIcon } from 'icons/AdultRoundIcon';
import { LockIcon } from 'icons/LockIcon';
import { DotDivider } from 'ui-components/common/DotDivider';
import { Link } from 'ui-components/common/Link';
import { IconCount, IconCountType } from 'ui-components/contents/IconCount';

type TitleFontSize = 'text-xs' | 'text-xxs' | 'text-xxxs';

interface ContentPrivateItemMetaProps {
  className?: string;
  content: ContentPrivateRelayDto;
  profile?: ProfileLitePublicRelayDto;
  iconCounts?: Record<IconCountType, number>;
  showCategoryType?: boolean;
  showDescription?: boolean;
  maxTitleLine?: number;
  titleFontSize?: TitleFontSize;
  href: string;
}

const Title = ({
  title,
  isPrivate,
  isAdult,
  showAdultIcon = false,
  maxTitleLine = 2,
  titleFontSize = 'text-xs',
}: {
  title: string;
  isPrivate: boolean;
  isAdult: boolean;
  showAdultIcon: boolean;
  maxTitleLine: number;
  titleFontSize: TitleFontSize;
}) => (
  <div className="flex items-center">
    {isPrivate && (
      <div className="flex mr-1">
        <LockIcon />
      </div>
    )}
    {showAdultIcon && isAdult && (
      <div className="flex mr-1">
        <AdultRoundIcon fillOpacity={0.3} className="size-4" />
      </div>
    )}
    <p
      className={`overflow-hidden text-ellipsis line-clamp-2 break-all break-words font-bold ${titleFontSize} ${maxTitleLine === 1 ? `line-clamp-1` : ''}`}>
      {title}
    </p>
  </div>
);

const Description = ({ description }: { description: string }) => (
  <p className="overflow-hidden text-ellipsis break-all break-words text-gray-700 font-regular text-xxxs line-clamp-2 text-desc">
    {description}
  </p>
);

const IconCountList = ({
  iconCountList,
  iconCounts,
  id,
}: {
  iconCountList: IconCountType[];
  iconCounts: Record<IconCountType, number>;
  id: number;
}) => (
  <div className="flex items-center gap-1 line-height-initial">
    {iconCountList.map((type, index) =>
      index !== iconCountList.length - 1 ? (
        <Fragment key={`${id}_${type}`}>
          <IconCount key={type} type={type} count={iconCounts[type]} color={Color.Gray400} />
          <DotDivider />
        </Fragment>
      ) : (
        <IconCount key={type} type={type} count={iconCounts[type]} color={Color.Gray400} />
      ),
    )}
  </div>
);

export const ContentPrivateItemMeta = ({
  className,
  content,
  profile,
  iconCounts,
  showCategoryType = false,
  showDescription = false,
  maxTitleLine = 2,
  titleFontSize = 'text-xs',
  href,
}: ContentPrivateItemMetaProps) => {
  const { title, categoryType, description, id } = content;
  const iconCountList = useMemo(() => (iconCounts ? (Object.keys(iconCounts) as IconCountType[]) : []), [iconCounts]);

  return (
    <div className={`flex flex-col gap-1 opacity-30 ${className}`}>
      <Link href={href}>
        <Title
          title={title}
          isPrivate={false}
          isAdult={false}
          showAdultIcon={false}
          titleFontSize={titleFontSize}
          maxTitleLine={maxTitleLine}
        />
      </Link>
      <div className="flex items-center text-gray-600 text-xxxs gap-1">
        <ProfileName profile={profile} />
        {showCategoryType && (
          <>
            <DotDivider />
            <p className="overflow-hidden text-ellipsis line-clamp-1 break-all break-words text-xxxs">
              {categoryType && CategoryTypeString[categoryType]}
            </p>
          </>
        )}
      </div>
      {showDescription && (
        <Link href={href}>
          <Description description={description} />
        </Link>
      )}
      {iconCounts && iconCountList.length > 0 && (
        <IconCountList iconCountList={iconCountList} iconCounts={iconCounts} id={id} />
      )}
    </div>
  );
};
